import { useParams, Link } from 'react-router-dom';
import { getArticleBySlug, getRelatedArticles, categories } from '@/mocks/articles';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/feature/ArticleCard';

export default function Article() {
  const { slug } = useParams<{ slug: string }>();
  const article = slug ? getArticleBySlug(slug) : undefined;

  if (!article) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background-50">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-primary-100 text-primary-500">
            <i className="ri-error-warning-line text-2xl"></i>
          </div>
          <h1 className="font-heading text-xl font-bold text-foreground-900 mb-2">文章未找到</h1>
          <p className="text-foreground-600 text-sm mb-6">你访问的文章不存在或已被移除</p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
          >
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  const relatedArticles = getRelatedArticles(article);
  const category = categories.find((c) => c.slug === article.categorySlug);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.image,
    author: {
      '@type': 'Organization',
      name: article.author,
    },
    datePublished: article.date,
    dateModified: article.date,
    publisher: {
      '@type': 'Organization',
      name: '传教士体位指南',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://www.chuanjiaoshi-tiwei.com/article/${article.slug}`,
    },
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      <main className="min-h-screen bg-background-50 pt-16">
        {}
        <div className="bg-background-100 border-b border-background-200/70">
          <div className="w-full px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                ...(category ? [{ label: category.name, href: `/category/${article.categorySlug}` }] : []),
                { label: article.title },
              ]}
            />
          </div>
        </div>

        {}
        <div className="w-full px-4 md:px-6 py-8 md:py-12">
          <div className="max-w-4xl mx-auto">
            <div className="flex flex-wrap items-center gap-2 mb-4">
              <Link
                to={`/category/${article.categorySlug}`}
                className="px-3 py-1 rounded-full text-xs font-medium bg-primary-100 text-primary-700 hover:bg-primary-200 transition-colors whitespace-nowrap"
              >
                {article.category}
              </Link>
              {article.tags.slice(0, 3).map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1 rounded-full text-xs bg-secondary-100 text-secondary-700 whitespace-nowrap"
                >
                  {tag}
                </span>
              ))}
            </div>

            <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-900 mb-4 leading-tight">
              {article.title}
            </h1>

            <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500 mb-8">
              <span className="flex items-center gap-1.5">
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-user-line"></i>
                </span>
                {article.author}
              </span>
              <span className="flex items-center gap-1.5">
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-calendar-line"></i>
                </span>
                {article.date}
              </span>
              <span className="flex items-center gap-1.5">
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-time-line"></i>
                </span>
                {article.readTime}
              </span>
            </div>

            {}
            <div className="mb-10 rounded-lg overflow-hidden">
              <img
                src={article.image}
                alt={article.title}
                title={article.title}
                className="w-full h-64 md:h-96 object-cover"
              />
            </div>

            {}
            <article
              className="article-content max-w-none mb-12"
              dangerouslySetInnerHTML={{ __html: article.contentHtml }}
            />

            {}
            <div className="border-t border-background-200/70 pt-8 mt-8">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm text-foreground-500 mr-2">标签：</span>
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1.5 rounded-full text-xs bg-background-100 text-foreground-600 border border-background-200/70 whitespace-nowrap"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>

            {}
            <div className="border-t border-background-200/70 pt-10 mt-10">
              <Link
                to={`/category/${article.categorySlug}`}
                className="inline-flex items-center gap-2 text-sm text-primary-500 hover:text-primary-600 font-medium whitespace-nowrap mb-6"
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-left-line"></i>
                </span>
                返回 {article.category} 分类
              </Link>
            </div>
          </div>
        </div>

        {}
        {relatedArticles.length > 0 && (
          <section className="w-full px-4 md:px-6 py-12 md:py-16 bg-background-100">
            <div className="max-w-4xl mx-auto">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-8">
                相关文章
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {relatedArticles.map((ra) => (
                  <ArticleCard key={ra.id} article={ra} />
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
    </>
  );
}